// Share / link / notification API helpers.
//
// Authenticated calls (shares, shared-with-me, notifications) go
// through the openapi-fetch ``api`` client so Bearer auth, 401
// refresh, and typed paths all come for free.
//
// Link-access (`POST /links/{token}`, `GET /links/{token}/download`)
// is anonymous — no auth, cookie-based unlock. We go raw-fetch
// there because the `api` client would try to attach a bearer token
// and we don't want one.

import { api, apiFetch } from "./client";
import type {
  CreateLinkShareResponse,
  LinkDownloadResponse,
  LinkFolderListResponse,
  LinkMetadataResponse,
  NotificationListResponse,
  PermissionLiteral,
  ShareListResponse,
  ShareResponse,
  SharedByMeResponse,
  SharedWithMeResponse,
} from "./types";
import { errorDetail } from "@/lib/apiError";

type Ok<T> = { kind: "ok"; value: T };
type Err = { kind: "error"; status: number; message: string };
export type Result<T = void> = Ok<T> | Err;

// ---------------------------------------------------------------------------
// Shares on a specific file
// ---------------------------------------------------------------------------

export async function listSharesFor(
  fileId: string,
): Promise<Result<ShareListResponse>> {
  const { data, error, response } = await api.GET("/api/v1/shares", {
    params: { query: { file_id: fileId } },
  });
  if (error || !data) {
    return {
      kind: "error",
      status: response?.status ?? 0,
      message: errorDetail(error, "could not load shares"),
    };
  }
  return { kind: "ok", value: data as ShareListResponse };
}

export async function createUserShare(params: {
  fileId: string;
  granteeEmail: string;
  permissions: PermissionLiteral[];
  message?: string | null;
}): Promise<Result<ShareResponse>> {
  const { data, error, response } = await api.POST("/api/v1/shares", {
    body: {
      type: "user",
      file_id: params.fileId,
      grantee_email: params.granteeEmail,
      permissions: params.permissions,
      message: params.message ?? null,
    },
  });
  if (error || !data) {
    return {
      kind: "error",
      status: response?.status ?? 0,
      message: errorDetail(error, "could not create share"),
    };
  }
  return { kind: "ok", value: data as ShareResponse };
}

export async function createGroupShare(params: {
  fileId: string;
  groupId: string;
  permissions: PermissionLiteral[];
}): Promise<Result<ShareResponse>> {
  const { data, error, response } = await api.POST("/api/v1/shares", {
    body: {
      type: "group",
      file_id: params.fileId,
      grantee_group_id: params.groupId,
      permissions: params.permissions,
    },
  });
  if (error || !data) {
    return {
      kind: "error",
      status: response?.status ?? 0,
      message: errorDetail(error, "could not create share"),
    };
  }
  return { kind: "ok", value: data as ShareResponse };
}

export async function createLinkShare(params: {
  fileId: string;
  permissions: PermissionLiteral[];
  password?: string | null;
  expiresAt?: string | null;
  downloadLimit?: number | null;
  message?: string | null;
}): Promise<Result<CreateLinkShareResponse>> {
  const { data, error, response } = await api.POST("/api/v1/shares", {
    body: {
      type: "link",
      file_id: params.fileId,
      permissions: params.permissions,
      password: params.password ?? null,
      expires_at: params.expiresAt ?? null,
      download_limit: params.downloadLimit ?? null,
      message: params.message ?? null,
    },
  });
  if (error || !data) {
    return {
      kind: "error",
      status: response?.status ?? 0,
      message: errorDetail(error, "could not create link"),
    };
  }
  return { kind: "ok", value: data as CreateLinkShareResponse };
}

export async function revokeShare(shareId: string): Promise<Result> {
  const { error, response } = await api.DELETE("/api/v1/shares/{share_id}", {
    params: { path: { share_id: shareId } },
  });
  if (error) {
    return {
      kind: "error",
      status: response?.status ?? 0,
      message: errorDetail(error, "could not revoke share"),
    };
  }
  return { kind: "ok", value: undefined };
}

// ---------------------------------------------------------------------------
// Shared with me
// ---------------------------------------------------------------------------

export async function fetchSharedWithMe(): Promise<
  Result<SharedWithMeResponse>
> {
  // openapi-fetch's type overload for zero-parameter paths trips the
  // TS compiler into narrowing the call return to `never` in this
  // project's strict config. ``apiFetch`` keeps the same auth +
  // refresh-on-401 behaviour without fighting the overload.
  const res = await apiFetch("/api/v1/shared-with-me");
  if (!res.ok) {
    return {
      kind: "error",
      status: res.status,
      message: "could not load shared items",
    };
  }
  return {
    kind: "ok",
    value: (await res.json()) as SharedWithMeResponse,
  };
}

// ---------------------------------------------------------------------------
// Shared by me (ADR 0042)
// ---------------------------------------------------------------------------

export async function fetchSharedByMe(): Promise<Result<SharedByMeResponse>> {
  // apiFetch for the same reason as fetchSharedWithMe above.
  const res = await apiFetch("/api/v1/shared-by-me");
  if (!res.ok) {
    return {
      kind: "error",
      status: res.status,
      message: "could not load shared items",
    };
  }
  return { kind: "ok", value: (await res.json()) as SharedByMeResponse };
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export async function fetchNotifications(params: {
  unreadOnly?: boolean;
  limit?: number;
}): Promise<Result<NotificationListResponse>> {
  const { data, error, response } = await api.GET("/api/v1/notifications", {
    params: {
      query: {
        unread_only: params.unreadOnly ?? false,
        limit: params.limit ?? 50,
      },
    },
  });
  if (error || !data) {
    return {
      kind: "error",
      status: response?.status ?? 0,
      message: errorDetail(error, "could not load notifications"),
    };
  }
  return { kind: "ok", value: data as NotificationListResponse };
}

export async function markAllNotificationsRead(): Promise<
  Result<{ markedCount: number }>
> {
  // Same zero-parameter narrowing as fetchSharedWithMe - openapi-fetch's
  // type overload trips on this path under our strict tsconfig, so we
  // use ``apiFetch``.
  const res = await apiFetch("/api/v1/notifications/read-all", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    return {
      kind: "error",
      status: res.status,
      message: "could not mark all read",
    };
  }
  const body = (await res.json()) as { marked_count: number };
  return { kind: "ok", value: { markedCount: body.marked_count } };
}

export async function markNotificationRead(
  notificationId: string,
): Promise<Result> {
  const { error, response } = await api.POST(
    "/api/v1/notifications/{notification_id}/read",
    { params: { path: { notification_id: notificationId } } },
  );
  if (error) {
    return {
      kind: "error",
      status: response?.status ?? 0,
      message: errorDetail(error, "could not mark notification read"),
    };
  }
  return { kind: "ok", value: undefined };
}

// ---------------------------------------------------------------------------
// Public link access — anonymous, NO bearer token
// ---------------------------------------------------------------------------

export type LinkAccessResult =
  | { kind: "ok"; value: LinkMetadataResponse }
  | { kind: "password_required"; sharedByDisplayName: string }
  | { kind: "expired"; message: string | null }
  | { kind: "not_found" }
  | { kind: "rate_limited"; retryAfterSeconds: number }
  | { kind: "bad_password" }
  | { kind: "error"; status: number; message: string };

export async function openLink(
  token: string,
  password: string | null,
): Promise<LinkAccessResult> {
  const res = await fetch(`/api/v1/links/${encodeURIComponent(token)}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password: password ?? null }),
  });
  if (res.status === 200) {
    return { kind: "ok", value: (await res.json()) as LinkMetadataResponse };
  }
  if (res.status === 401) {
    const body = (await res
      .json()
      .catch(() => ({}) as Record<string, unknown>)) as {
      password_required?: boolean;
      shared_by_display_name?: string;
      detail?: string;
    };
    if (body.password_required) {
      return {
        kind: "password_required",
        sharedByDisplayName: body.shared_by_display_name ?? "",
      };
    }
    return { kind: "bad_password" };
  }
  if (res.status === 404) return { kind: "not_found" };
  if (res.status === 410) {
    const body = (await res
      .json()
      .catch(() => ({}) as Record<string, unknown>)) as {
      detail?: string;
    };
    return { kind: "expired", message: errorDetail(body, "") || null };
  }
  if (res.status === 429) {
    const retry = res.headers.get("retry-after");
    return {
      kind: "rate_limited",
      retryAfterSeconds: retry ? Number(retry) : 60,
    };
  }
  return {
    kind: "error",
    status: res.status,
    message: `unexpected status ${res.status}`,
  };
}

export type LinkDownloadResult =
  | { kind: "ok"; value: LinkDownloadResponse }
  | { kind: "needs_unlock" }
  | { kind: "expired"; message: string | null }
  | { kind: "not_found" }
  | { kind: "error"; status: number; message: string };

/**
 * A download URL for the shared file, or with ``fileId`` for a file
 * inside a shared folder (ADR 0043). Each use counts as a download.
 */
export async function downloadViaLink(
  token: string,
  fileId?: string,
): Promise<LinkDownloadResult> {
  const base = `/api/v1/links/${encodeURIComponent(token)}/download`;
  const res = await fetch(
    fileId ? `${base}/${encodeURIComponent(fileId)}` : base,
    { credentials: "include" },
  );
  if (res.status === 200) {
    return { kind: "ok", value: (await res.json()) as LinkDownloadResponse };
  }
  if (res.status === 401) return { kind: "needs_unlock" };
  if (res.status === 404) return { kind: "not_found" };
  if (res.status === 410) {
    const body = (await res
      .json()
      .catch(() => ({}) as Record<string, unknown>)) as {
      detail?: string;
    };
    return { kind: "expired", message: errorDetail(body, "") || null };
  }
  return {
    kind: "error",
    status: res.status,
    message: `unexpected status ${res.status}`,
  };
}

/** Thrown by ``fetchLinkFolder`` with the failed link result. */
export class LinkFolderError extends Error {
  constructor(readonly result: Exclude<LinkDownloadResult, { kind: "ok" }>) {
    super(result.kind);
  }
}

/**
 * One page of a folder link: the shared folder, or ``parentId`` below
 * it. Browsing doesn't count as a download.
 */
export async function fetchLinkFolder(
  token: string,
  parentId: string | null,
  cursor: string | null,
): Promise<LinkFolderListResponse> {
  const params = new URLSearchParams();
  if (parentId) params.set("parent_id", parentId);
  if (cursor) params.set("cursor", cursor);
  const query = params.size > 0 ? `?${params.toString()}` : "";
  const res = await fetch(
    `/api/v1/links/${encodeURIComponent(token)}/contents${query}`,
    { credentials: "include" },
  );
  if (res.ok) return (await res.json()) as LinkFolderListResponse;
  if (res.status === 401) throw new LinkFolderError({ kind: "needs_unlock" });
  if (res.status === 410) {
    const body = (await res
      .json()
      .catch(() => ({}) as Record<string, unknown>)) as { detail?: string };
    throw new LinkFolderError({
      kind: "expired",
      message: errorDetail(body, "") || null,
    });
  }
  if (res.status === 404) throw new LinkFolderError({ kind: "not_found" });
  throw new LinkFolderError({
    kind: "error",
    status: res.status,
    message: `HTTP ${res.status}`,
  });
}

/** Thrown by ``fetchLinkContent`` with the failed link result. */
export class LinkContentError extends Error {
  constructor(readonly result: Exclude<LinkDownloadResult, { kind: "ok" }>) {
    super(result.kind);
  }
}

/**
 * The shared file's bytes, for the link page's preview. Each call is a
 * full download as far as the link's download limit goes (the server
 * counts every non-resumed GET), so callers must not retry or refetch.
 */
export async function fetchLinkContent(
  token: string,
): Promise<Uint8Array<ArrayBuffer>> {
  const r = await downloadViaLink(token);
  if (r.kind !== "ok") throw new LinkContentError(r);
  const res = await fetch(r.value.url, { credentials: "same-origin" });
  if (!res.ok) {
    throw new LinkContentError(
      res.status === 403
        ? { kind: "expired", message: null }
        : { kind: "error", status: res.status, message: `HTTP ${res.status}` },
    );
  }
  return new Uint8Array(await res.arrayBuffer());
}
